/**
 * /wireline-slickline/units/:id — one wireline unit and its crew templates
 * (GET / PATCH / DELETE /wireline-slickline/units/:id, fieldiq-api#1336).
 *
 * Panels stack full width: the unit's details, then its crews, then the edit
 * form when editing (never a modal for the record itself).
 *
 * Edit and delete are WL_ADMIN (admin, office_staff); crews are WL_CREW_WRITE
 * (see UnitCrews). Delete asks in-app first, and a unit that still has a crew
 * is refused by the API (409 unit_has_crews) with a sentence — shown in the
 * dialog, never as a green toast.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Gauge, Pencil, Trash2, Users, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDeleteWlUnit, useUpdateWlUnit, useWlEquipmentOptions, useWlMeta, useWlUnit, useWlUnits } from '../../hooks/useWirelineSlickline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminWl, canWriteWlCrews, existingIdOf, wlRefusal, wlWithUnit } from '../../lib/wireline-slickline';
import { useBackToList } from '../downhole/recordPage';
import { UnitCrews } from './UnitCrews';
import { UnitForm } from './UnitForm';
import { isNotFound, WL_BAND_SECONDARY, WL_EYEBROW, WL_PRIMARY, WL_SECONDARY, WlDialog, WlEmpty, WlFormError, WlLoading, WlPanel, WlSpec } from './WlBits';

export default function WlUnitDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/units');
  const { user } = useAuth();
  const query = useWlUnit(id);
  const catalog = useWlMeta().data?.data ?? null;
  const canAdmin = canAdminWl(user?.role);
  const canCrews = canWriteWlCrews(user?.role);
  const [editing, setEditing] = useState(false);
  const allUnits = useWlUnits();
  const equipment = useWlEquipmentOptions(canAdmin && editing);
  const update = useUpdateWlUnit();
  const del = useDeleteWlUnit();
  const [editError, setEditError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The duplicate link opens another unit on this same route: start it closed.
  useEffect(() => {
    setEditing(false);
    setEditError(null);
    setDuplicateOf(null);
    setDeleting(false);
  }, [id]);

  const unit = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const lineTypes = unit ? (unit.lineTypeLabels?.length ? unit.lineTypeLabels : unit.lineTypes ?? []).join(', ') || '—' : '—';

  return (
    <div data-testid="wl-unit-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={unit ? unit.unitNumber || '—' : query.isLoading ? 'Unit' : '—'}
        onBack={back}
        backLabel="Back"
        actions={
          unit && canAdmin ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="fi-action fi-action--standard fi-action--primary"
                onClick={() => {
                  setEditError(null);
                  setDuplicateOf(null);
                  setEditing(true);
                }}
                disabled={editing}
              >
                <Pencil className="size-4" aria-hidden="true" />
                Edit unit
              </button>
              <button type="button" className={WL_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Wrench className="size-4" aria-hidden="true" />, label: 'Drums', tone: 'neutral', loading: query.isLoading, value: displayValue(unit?.drumCount) },
          { icon: <Gauge className="size-4" aria-hidden="true" />, label: 'Mast', tone: 'neutral', loading: query.isLoading, value: unit ? wlWithUnit(unit.mastRating, unit.mastRatingUnit) : '—' },
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Crews', tone: 'neutral', loading: query.isLoading, value: displayValue(unit?.crews?.length) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading unit" />
          ) : notFound || (!failure && !unit) ? (
            <WlPanel eyebrow="Unit" title="Unit">
              <WlEmpty>This unit was not found. It may have been deleted.</WlEmpty>
            </WlPanel>
          ) : failure || !unit ? (
            <WlPanel eyebrow="Unit" title="Unit">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this unit" />
            </WlPanel>
          ) : (
            <>
              <WlPanel eyebrow="Unit" title="Details" testId="wl-unit-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WlSpec label="Unit number" value={unit.unitNumber || '—'} mono />
                  <WlSpec
                    label="Equipment unit"
                    value={
                      <Link to={`/equipment/${unit.equipmentId}`} className="underline">
                        {unit.equipmentName || '—'}
                        {unit.equipmentUnitNumber ? ` · ${unit.equipmentUnitNumber}` : ''}
                      </Link>
                    }
                  />
                  <WlSpec label="Serial number" value={unit.serialNumber || '—'} mono />
                  <WlSpec label="Unit status" value={unit.equipmentStatus || '—'} />
                  <WlSpec label="Line types" value={lineTypes} />
                  <WlSpec label="Drums" value={displayValue(unit.drumCount)} mono />
                  <WlSpec label="Mast" value={unit.mastKindLabel ?? unit.mastKind ?? '—'} />
                  <WlSpec
                    label="Mast equipment"
                    value={
                      unit.mastEquipmentId ? (
                        <Link to={`/equipment/${unit.mastEquipmentId}`} className="underline">
                          {unit.mastEquipmentName || '—'}
                        </Link>
                      ) : (
                        '—'
                      )
                    }
                  />
                  <WlSpec label="Mast rating" value={wlWithUnit(unit.mastRating, unit.mastRatingUnit)} mono />
                  <WlSpec label="Max depth rating" value={wlWithUnit(unit.maxDepthRating, unit.maxDepthRatingUnit)} mono />
                </dl>
                {unit.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{unit.notes}</p>}
              </WlPanel>

              <UnitCrews unit={unit} catalog={catalog} canWrite={canCrews} />

              {editing && canAdmin && (
                <WlPanel eyebrow="Edit" title="Edit unit" testId="wl-unit-edit">
                  {equipment.isLoading ? (
                    <WlLoading label="Loading equipment" />
                  ) : (
                    <UnitForm
                      key={unit.id}
                      unit={unit}
                      catalog={catalog}
                      meta={meta}
                      equipment={equipment.data ?? []}
                      units={allUnits.data?.data ?? []}
                      pending={update.isPending}
                      error={editError}
                      duplicateOf={duplicateOf}
                      submitLabel="Save unit"
                      onCancel={() => setEditing(false)}
                      onSubmit={(body) => {
                        setEditError(null);
                        setDuplicateOf(null);
                        update.mutate(
                          { id: unit.id, body },
                          {
                            onSuccess: () => {
                              toast.success('Unit saved');
                              setEditing(false);
                            },
                            onError: (err) => {
                              setEditError(wlRefusal(err, 'The unit could not be saved.'));
                              setDuplicateOf(existingIdOf(err, 'existingUnitId'));
                            },
                          },
                        );
                      }}
                    />
                  )}
                </WlPanel>
              )}
            </>
          )}
        </div>
      </section>

      {unit && canAdmin && (
        <WlDialog
          open={deleting}
          title="Delete unit"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep unit
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(unit.id, {
                    onSuccess: () => {
                      toast.success('Unit deleted');
                      navigate('/wireline-slickline/units', { replace: true });
                    },
                    onError: (err) => setDeleteError(wlRefusal(err, 'The unit could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete unit'}
              </button>
            </>
          }
        >
          <WlFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">
            Removes {unit.unitNumber} from the wireline register. Its truck or skid stays in Equipment.
            {unit.crews.length > 0 ? ' A unit with crews cannot be deleted: delete its crews first.' : ''}
          </p>
        </WlDialog>
      )}
    </div>
  );
}
